import { v4 } from 'uuid';
import ls from 'localstorage-slim';
import type { Signal } from 'typed-signals';
import type { PlatformTournaments, TournamentCreateConfig, TournamentData } from '../Platform';
import { MS_IN_DAY } from '../../../utils/time-utils';
import { openFakeTournamentCreateDialog } from './ui/openFakeTournamentCreateDialog';
import { openFakeTournamentShareDialog } from './ui/openFakeTournamentShareDialog';
import { openFakeTournamentJoinDialog } from './ui/openFakeTournamentJoinDialog';
import { waitForMs } from '../../../utils/async-utils';

export class FakeTournaments implements PlatformTournaments {
  private static readonly localStorageKey = 'hexa-dev-fakeTournaments';

  private static readonly localStorageEntryKey = 'hexa-dev-fakeTournamentsEntry';

  private currentTournament: TournamentData | null = null;

  private currentContext: string | null = null;

  private tournaments: Record<string, TournamentData> = {};

  public static debugClearTournaments(): void {
    ls.remove(this.localStorageKey);
  }

  public static setEntryTournamentForNextSession(): void {
    ls.set(FakeTournaments.localStorageEntryKey, true);
  }

  constructor(onContextChanged?: Signal<(contextId: string | null) => void>) {
    this.tournaments = ls.get(FakeTournaments.localStorageKey) ?? {};
    void this.fetchGlobalTournament();
    this.fetchEntryTournament();

    onContextChanged?.connect((contextId: string | null) => {
      this.changeContext(contextId);
    });
  }

  private changeContext(contextId: string | null): void {
    this.currentContext = contextId;
    this.currentTournament =
      Object.values(this.tournaments).find((tournament) => tournament.contextId === contextId) ?? null;
  }

  private fetchEntryTournament(): void {
    if (!ls.get(FakeTournaments.localStorageEntryKey)) return;

    this.currentTournament = this.createFakeTournamentData({
      initialScore: 0,
      config: {
        title: `FT ${v4().slice(-3)}`,
      },
      data: {
        creatorId: v4(),
      },
    });
    this.currentContext = this.currentTournament.contextId;

    ls.remove(FakeTournaments.localStorageEntryKey);
  }

  private async fetchGlobalTournament(): Promise<void> {
    try {
      const response = await fetch(
        'https://firebasestorage.googleapis.com/v0/b/hexa-stack-production.appspot.com/o/global-tournament.json?alt=media',
      );
      const tournamentData = (await response.json()) as TournamentData;

      this.tournaments[tournamentData.tournamentId] = tournamentData;
      ls.set(FakeTournaments.localStorageKey, this.tournaments);
    } catch (e) {
      console.warn(e);
    }
  }

  private createFakeTournamentData(createConfig: TournamentCreateConfig): TournamentData {
    const tournamentData = {
      contextId: v4(),
      creatorId: (createConfig.data as Record<string, string>)?.tournamentCreatorId ?? null,
      endsAt: Date.now() + MS_IN_DAY * 7,
      payload: createConfig.data,
      title: createConfig.config?.title ?? 'Friendly tournament',
      tournamentId: v4(),
    };

    this.tournaments[tournamentData.tournamentId] = tournamentData;
    ls.set(FakeTournaments.localStorageKey, tournamentData);

    return tournamentData;
  }

  public async createTournament(config: TournamentCreateConfig): Promise<TournamentData | null> {
    const created = await openFakeTournamentCreateDialog(config);

    if (created) {
      this.currentTournament = this.createFakeTournamentData(config);
      this.currentContext = this.currentTournament.contextId;
      return this.currentTournament;
    }

    return null;
  }

  public async shareTournament(score: number, payload: Record<string, unknown>): Promise<boolean> {
    if (!this.currentTournament) return false;

    await waitForMs(3000);

    return openFakeTournamentShareDialog(score, this.currentTournament, payload);
  }

  public async getCurrentTournament(): Promise<TournamentData | null> {
    return this.currentTournament;
  }

  public async joinTournament(tournamentId: string): Promise<TournamentData | null> {
    const tournamentData = this.tournaments[tournamentId];
    if (!tournamentData) return null;

    const success = await openFakeTournamentJoinDialog(tournamentData);

    if (success) {
      this.currentTournament = tournamentData;
      this.currentContext = tournamentData.contextId;
      return tournamentData;
    }

    return null;
  }

  public async postScore(score: number): Promise<boolean> {
    console.log('postScore', score);
    return true;
  }

  public getContextID(): string | null {
    return this.currentContext;
  }
}
